import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { flattenFFEItems, isOrdered, isArrived, getDeliveryLocation, summarize, ITEM_STATUS_META, PHASE_ORDER, PHASE_LABELS } from '../utils/logisticsStatus';

const BACKEND_URL = (window.ENV?.REACT_APP_BACKEND_URL || window.location.origin) || window.location.origin;

const VENDOR_ORDER_URLS = {
  'Four Hands': 'https://fourhands.com/account/orders',
  'Rowe Furniture': 'https://rowefurniture.com/reports/order_status',
  'Visual Comfort': 'https://www.visualcomfort.com/orderview/orders/history/',
  'Uttermost': 'https://uttermost.com/account-dashboard/my-orders?page=1&perPage=10',
  'Regina Andrew': 'https://www.reginaandrew.com/app/my_account.ssp?fragment=overview#/purchases',
  'Loloi Rugs': 'https://www.loloirugs.com/account?view=order-history',
  'V&H': 'https://vandh.com/#/orders',
  'Crestview Collection': 'https://www.crestviewcollection.com/account-dashboard/my-orders',
  'Bassett Mirror': 'https://www.bassettmirror.com/customers/open_orders.cfm',
  'Safavieh': 'https://safavieh.com/my-orders',
  'York Wallcoverings': 'https://www.yorkwall.com/OrderHistory/Index',
  'Classic Home': 'https://www.classichome.com/pages/account-orders',
};

export default function CriticalPathDashboard({ projectId }) {
  const [project, setProject] = useState(null);
  const [orderedItems, setOrderedItems] = useState([]);
  const [groupedByVendor, setGroupedByVendor] = useState({});
  const [customVendorLinks, setCustomVendorLinks] = useState({});
  const [addingLinkFor, setAddingLinkFor] = useState(null);
  const [newLink, setNewLink] = useState('');
  
  useEffect(() => {
    loadProject();
    loadCustomLinks();
  }, [projectId]);
  
  const loadCustomLinks = () => {
    const saved = localStorage.getItem(`vendor_links_${projectId}`);
    if (saved) setCustomVendorLinks(JSON.parse(saved));
  };
  
  const saveCustomLink = (vendor) => {
    if (!newLink.trim()) return;
    
    const updated = { ...customVendorLinks, [vendor]: newLink };
    setCustomVendorLinks(updated);
    localStorage.setItem(`vendor_links_${projectId}`, JSON.stringify(updated));
    setAddingLinkFor(null);
    setNewLink('');
  };
  
  const updateItemTracking = async (itemId, field, value) => {
    try {
      await axios.put(`${BACKEND_URL}/api/items/${itemId}`, { [field]: value });
      loadProject(); // Refresh to sync with FFE
    } catch (error) {
      console.error('Failed to update item:', error);
    }
  };
  
  const loadProject = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/projects/${projectId}?sheet_type=ffe`);
      setProject(response.data);
      
      // Inclusive: every committed piece (APPROVED onward), classified off the
      // canonical FF&E item.status via the shared logisticsStatus helper.
      const ordered = flattenFFEItems(response.data).filter(isOrdered);
      setOrderedItems(ordered);
      
      const byVendor = {};
      ordered.forEach(item => {
        const vendor = item.vendor || 'Unknown';
        if (!byVendor[vendor]) byVendor[vendor] = [];
        byVendor[vendor].push(item);
      });
      setGroupedByVendor(byVendor);
      
    } catch (error) {
      console.error('Failed to load project:', error);
    }
  };

  const summary = summarize(orderedItems);
  const byLocation = orderedItems.filter(isArrived).reduce((acc, i) => {
    const loc = getDeliveryLocation(i);
    acc[loc] = (acc[loc] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0F172A] via-[#1E293B] to-[#0F172A] p-6">
      <div className="bg-gradient-to-r from-[#1E293B] via-[#0F172A] to-[#1E293B] p-6 border-b-4 border-[#D4A574] shadow-2xl relative overflow-hidden mb-6">
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#D4A574]/10 to-transparent opacity-50"></div>
        <div className="relative z-10">
          <h1 className="text-4xl font-bold text-[#D4A574] drop-shadow-lg">📊 CRITICAL PATH & ORDER TRACKING</h1>
          <p className="text-[#D4C5A9] text-lg mt-2">Track all ordered items and vendor deliveries</p>
        </div>
      </div>
      
      {/* Summary tiles (agree with Shipping & Delivery views) */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-gradient-to-br from-gray-900 to-black rounded-xl p-6 border-2 border-[#D4A574]/30">
          <h3 className="text-[#D4A574] font-bold text-lg mb-2">Total Ordered</h3>
          <div className="text-5xl font-bold text-[#D4C5A9]">{summary.total}</div>
        </div>
        <div className="bg-gradient-to-br from-gray-900 to-black rounded-xl p-6 border-2 border-blue-500/30">
          <h3 className="text-blue-300 font-bold text-lg mb-2">In Transit</h3>
          <div className="text-5xl font-bold text-blue-200">{summary.inTransit}</div>
        </div>
        <div className="bg-gradient-to-br from-gray-900 to-black rounded-xl p-6 border-2 border-purple-500/30">
          <h3 className="text-purple-300 font-bold text-lg mb-2">Arrived / Delivered</h3>
          <div className="text-5xl font-bold text-purple-200">{summary.arrived}</div>
        </div>
        <div className="bg-gradient-to-br from-gray-900 to-black rounded-xl p-6 border-2 border-orange-500/30">
          <h3 className="text-orange-300 font-bold text-lg mb-2">Awaiting Delivery</h3>
          <div className="text-5xl font-bold text-orange-200">{summary.awaiting}</div>
        </div>
      </div>

      {/* Full status breakdown by phase — every delivery status preserved */}
      <div className="bg-gradient-to-br from-gray-900 to-black rounded-xl p-6 border-2 border-[#D4A574]/30 mb-6">
        <h3 className="text-[#D4A574] font-bold text-xl mb-4">Status Breakdown</h3>
        {PHASE_ORDER.map(phase => {
          const metas = ITEM_STATUS_META.filter(m => m.phase === phase && (summary.byStatus[m.status] || 0) > 0);
          if (metas.length === 0) return null;
          return (
            <div key={phase} className="mb-4">
              <div className="text-xs uppercase tracking-wider text-gray-500 mb-2">{PHASE_LABELS[phase]}</div>
              <div className="flex flex-wrap gap-3">
                {metas.map(m => (
                  <div key={m.status} className="flex items-center gap-2 px-3 py-2 rounded-lg"
                       style={{ background: `${m.color}22`, border: `1px solid ${m.color}55` }}>
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: m.color }}></span>
                    <span className="text-[#D4C5A9] text-sm">{m.status}</span>
                    <span className="font-bold text-white ml-1">{summary.byStatus[m.status]}</span>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
        {summary.total === 0 && (
          <p className="text-gray-500 text-sm">No ordered items yet.</p>
        )}
      </div>

      {/* Delivery locations — the several places items land */}
      {Object.keys(byLocation).length > 0 && (
        <div className="bg-gradient-to-br from-gray-900 to-black rounded-xl p-6 border-2 border-[#D4A574]/30 mb-6">
          <h3 className="text-[#D4A574] font-bold text-xl mb-4">Where Items Landed</h3>
          <div className="flex flex-wrap gap-3">
            {Object.entries(byLocation).map(([loc, count]) => (
              <div key={loc} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#D4A574]/10 border border-[#D4A574]/30">
                <span className="text-[#D4C5A9]">📍 {loc}</span>
                <span className="font-bold text-white">{count}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      
      <div className="space-y-6">
        {Object.keys(groupedByVendor).map(vendor => (
          <div key={vendor} className="bg-gradient-to-br from-gray-900 to-black rounded-xl p-6 border-2 border-[#D4A574]/30">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold text-[#D4A574]">{vendor}</h2>
              <div className="flex gap-3">
                {(VENDOR_ORDER_URLS[vendor] || customVendorLinks[vendor]) ? (
                  <a
                    href={customVendorLinks[vendor] || VENDOR_ORDER_URLS[vendor]}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-6 py-3 bg-gradient-to-r from-[#D4A574] to-[#BCA888] hover:from-[#E4B584] hover:to-[#C49564] text-black rounded-lg font-bold"
                  >
                    🔗 View {vendor} Orders
                  </a>
                ) : (
                  <button
                    onClick={() => setAddingLinkFor(vendor)}
                    className="px-6 py-3 bg-gray-700 hover:bg-gray-600 text-[#D4C5A9] rounded-lg font-bold"
                  >
                    + Add Order Tracking Link
                  </button>
                )}
                
                {addingLinkFor === vendor && (
                  <div className="flex gap-2">
                    <input
                      type="url"
                      value={newLink}
                      onChange={(e) => setNewLink(e.target.value)}
                      placeholder="https://vendor.com/orders"
                      className="px-4 py-2 bg-gray-800 text-white border border-[#D4A574]/50 rounded"
                    />
                    <button
                      onClick={() => saveCustomLink(vendor)}
                      className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded font-bold"
                    >
                      Save
                    </button>
                    <button
                      onClick={() => { setAddingLinkFor(null); setNewLink(''); }}
                      className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded"
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            </div>
            
            <div className="space-y-3">
              {groupedByVendor[vendor].map((item, idx) => (
                <div key={idx} className="bg-gray-800 rounded-lg p-4 border border-[#D4A574]/20">
                  <div className="grid grid-cols-6 gap-4">
                    <div>
                      <div className="text-sm text-gray-400">Item</div>
                      <div className="text-[#D4C5A9] font-semibold">{item.name}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-400">Room</div>
                      <div className="text-[#D4C5A9]">{item.room_name}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-400">Status</div>
                      <select
                        value={item.status || ''}
                        onChange={(e) => updateItemTracking(item.id, 'status', e.target.value)}
                        className="bg-gray-700 text-[#D4A574] px-2 py-1 rounded border border-[#D4A574]/30"
                      >
                        <option value="">—</option>
                        {ITEM_STATUS_META.map(m => (
                          <option key={m.status} value={m.status}>{m.status}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <div className="text-sm text-gray-400">Tracking #</div>
                      <input
                        type="text"
                        value={item.tracking_number || ''}
                        onChange={(e) => updateItemTracking(item.id, 'tracking_number', e.target.value)}
                        className="bg-gray-700 text-[#D4C5A9] px-2 py-1 rounded border border-[#D4A574]/30 w-full"
                        placeholder="Enter tracking #"
                      />
                    </div>
                    <div>
                      <div className="text-sm text-gray-400">Carrier</div>
                      <input
                        type="text"
                        value={item.carrier || ''}
                        onChange={(e) => updateItemTracking(item.id, 'carrier', e.target.value)}
                        className="bg-gray-700 text-[#D4C5A9] px-2 py-1 rounded border border-[#D4A574]/30 w-full"
                        placeholder="FedEx, UPS, etc."
                      />
                    </div>
                    <div>
                      <div className="text-sm text-gray-400">ETA</div>
                      <input
                        type="date"
                        value={(item.expected_delivery || '').toString().slice(0, 10)}
                        onChange={(e) => updateItemTracking(item.id, 'expected_delivery', e.target.value || null)}
                        className="bg-gray-700 text-[#D4C5A9] px-2 py-1 rounded border border-[#D4A574]/30"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
